// 认证Hook - 使用Mock API
import { useState, useEffect, createContext, useContext } from 'react';
import api from '@/lib/api-client';
import type { User } from '@/services/mock-api';

interface AuthSession {
  user: User;
  token: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  sessions: AuthSession[];
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, userData: Partial<User>) => Promise<void>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<User>) => Promise<void>;
  switchAccount: (userId: string) => Promise<void>;
  addAccount: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState<AuthSession[]>([]);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      let restored: AuthSession | undefined;

      try {
        const savedSessions = localStorage.getItem('auth_sessions');
        if (savedSessions) {
          const parsed = JSON.parse(savedSessions) as AuthSession[];
          if (!cancelled) setSessions(parsed);

          const currentUserId = localStorage.getItem('current_user_id');
          restored = parsed.find((s) => s.user.id === currentUserId);
          if (restored) {
            localStorage.setItem('access_token', restored.token);
            if (!cancelled) setUser(restored.user);
          }
        }

        const currentUser = await api.auth.getCurrentUser();
        if (cancelled) return;

        if (currentUser) {
          setUser(currentUser);
          const token = localStorage.getItem('access_token') || '';
          updateSessions(currentUser, token);
        } else if (!restored) {
          setUser(null);
        }
      } catch (e) {
        console.error('Auth init failed:', e);
        if (!cancelled && !restored) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅挂载时做一次会话恢复与 /me 校验
  }, []);

  const updateSessions = (newUser: User, newToken: string) => {
    setSessions(prev => {
      const rest = prev.filter(s => s.user.id !== newUser.id);
      const newSessions = [...rest, { user: newUser, token: newToken }];
      localStorage.setItem('auth_sessions', JSON.stringify(newSessions));
      return newSessions;
    });
    localStorage.setItem('current_user_id', newUser.id);
    localStorage.setItem('access_token', newToken);
  };

  async function signIn(email: string, password: string) {
    setLoading(true);
    try {
      const data = await api.auth.signIn(email, password);
      setUser(data.user);
      const token = ('access_token' in data ? data.access_token : '') || localStorage.getItem('access_token') || '';
      updateSessions(data.user, token);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }

  async function signUp(email: string, password: string, userData: Partial<User>) {
    setLoading(true);
    try {
      await api.auth.signUp(email, password, userData);
      // 注册成功后自动登录以获取 access_token
      const loginData = await api.auth.signIn(email, password);
      setUser(loginData.user);
      const token = ('access_token' in loginData ? loginData.access_token : '') || localStorage.getItem('access_token') || '';
      updateSessions(loginData.user, token);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }

  async function signOut() {
    setLoading(true);
    try {
      // 从会话列表中移除当前用户
      const userId = user?.id;
      setSessions(prev => {
        const newSessions = prev.filter(s => s.user.id !== userId);
        localStorage.setItem('auth_sessions', JSON.stringify(newSessions));
        return newSessions;
      });
      
      await api.auth.signOut();
      localStorage.removeItem('current_user_id');
      setUser(null);
      
      // 如果还有其他会话，自动切换到第一个
      const remainingSessions = sessions.filter(s => s.user.id !== userId);
      if (remainingSessions.length > 0) {
        await switchAccount(remainingSessions[0].user.id);
      }
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setLoading(false);
    }
  }

  async function updateProfile(updates: Partial<User>) {
    if (!user) return;

    try {
      const updated = await api.auth.updateProfile(user.id, updates);
      if (updated) {
        setUser(updated);
        // 同时更新会话列表中的缓存
        const newToken = localStorage.getItem('access_token') || '';
        updateSessions(updated, newToken);
      }
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }

  const switchAccount = async (userId: string) => {
    const session = sessions.find(s => s.user.id === userId);
    if (!session) return;

    setLoading(true);
    try {
      // 1) 先切 token
      localStorage.setItem('access_token', session.token);

      // 2) 再以后端 /auth/me 为准，确保“显示账号 == token账号”
      const currentUser = await api.auth.getCurrentUser();
      if (!currentUser) {
        throw new Error('切换账号失败：Token无效，请重新登录');
      }

      // 3) 写入当前活跃账号并刷新会话缓存中的用户快照
      setUser(currentUser);
      updateSessions(currentUser, session.token);
      localStorage.setItem('current_user_id', currentUser.id);

      // 保持你现有的“全局刷新确保页面数据重拉”
      window.location.reload();
    } catch (error) {
      console.error('Switch account error:', error);
      alert(error instanceof Error ? error.message : '切换账号失败');
    } finally {
      setLoading(false);
    }
  };

  const addAccount = () => {
    // 这里简单跳转到登录页，并带上标记
    window.location.href = '/login?mode=add_account';
  };

  const value = {
    user,
    loading,
    sessions,
    signIn,
    signUp,
    signOut,
    updateProfile,
    switchAccount,
    addAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
